/**
 * Plan answers a browser kept before answers were saved on the server.
 *
 * Until 2026-11-02 a plan gate's answers were staged in the browser, under
 * `yagni.planAnswers.<gateId>`, and sent with Approve or Send back. Answers
 * are saved on the server as they are given now (spec 2026-11-02, "The plan a
 * person approves is the only contract"). These two exist for the one-time
 * move only: on the first load of an open gate with nothing saved, the sheet
 * posts what this browser kept (`useTicket`), then clears it. Delete them once
 * no gate opened before the deploy is still open.
 *
 * Every access is guarded: a private window, a full quota or blocked site
 * data reads as nothing kept.
 */
import type { StagedAnswer } from "./planQuestions.js";

const KEY_PREFIX = "yagni.planAnswers.";

export function stagedPlanAnswersKey(gateId: string): string {
  return KEY_PREFIX + gateId;
}

function isStagedAnswer(value: unknown): value is StagedAnswer {
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.questionId === "string" &&
    typeof entry.question === "string" &&
    typeof entry.answer === "string" &&
    typeof entry.why === "string"
  );
}

/** What this browser kept for the gate, or nothing. */
export function readStagedPlanAnswers(gateId: string): StagedAnswer[] {
  try {
    const raw = window.localStorage.getItem(stagedPlanAnswersKey(gateId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isStagedAnswer) : [];
  } catch (err) {
    console.warn("[plan answers] could not read kept answers for gate", gateId, err);
    return [];
  }
}

export function clearStagedPlanAnswers(gateId: string): void {
  try {
    window.localStorage.removeItem(stagedPlanAnswersKey(gateId));
  } catch (err) {
    console.warn("[plan answers] could not clear kept answers for gate", gateId, err);
  }
}
